import { getPeriodOrder } from "@/components/PriceUpdate/periodSorter";
import type { ReadSparkspreadPricesApiV1SparkspreadGetResponse } from "@/client/types.gen";
import { DataPoint } from "./MultiLine";
import { SparkSpreadFuturesContract } from "./SparkSpreadFuturesContractSelect";
import { SparkSpreadFuturesContractType } from "./SparkSpreadFuturesContractTypesSelect";
import { SparkSpreadProduct } from "./SparkSpreadProductSelect";

type TransformSpspQueryToMultiChartData = {
  data: ReadSparkspreadPricesApiV1SparkspreadGetResponse;
  dateRange: {
    from: Date;
    to: Date;
  };
  selectedSpread: SparkSpreadProduct;
  selectedContracts: SparkSpreadFuturesContract[];
};
type TransformedDataPoint = {
  timestamp: number;
  [key: string]: number | string;
};

export function transformSpspQueryToMultiChartData({
  data,
  dateRange,
  selectedSpread,
  selectedContracts,
}: TransformSpspQueryToMultiChartData): Record<string, TransformedDataPoint[]> {
  const groupedByProduct = data.data
    .filter(
      (item) =>
        item.Product === selectedSpread &&
        selectedContracts.includes(item.Period) &&
        item.Timestamp >= new Date(dateRange.from).getTime() &&
        item.Timestamp <= new Date(dateRange.to).getTime(),
    )
    .reduce<Record<string, TransformedDataPoint[]>>((acc, item) => {
      if (!acc[item.Product]) {
        acc[item.Product] = [];
      }

      const existingEntry = acc[item.Product].find((entry) => entry.timestamp === item.Timestamp);
      if (existingEntry) {
        existingEntry[item.Period] = item.Price;
        if (item.source_type) {
          existingEntry[`${item.Period}_source`] = item.source_type;
        }
      } else {
        const newEntry: TransformedDataPoint = {
          timestamp: item.Timestamp,
          [item.Period]: item.Price,
        };
        if (item.source_type) {
          newEntry[`${item.Period}_source`] = item.source_type;
        }
        acc[item.Product].push(newEntry);
      }

      return acc;
    }, {});

  return groupedByProduct;
}

type SupportingProduct = "EUA" | "TTF" | "Base NL" | "Peak NL";

type TransformSettlementsToMultiChartData = {
  data: ReadSparkspreadPricesApiV1SparkspreadGetResponse;
  dateRange: {
    from: Date;
    to: Date;
  };
  selectedContracts: SparkSpreadFuturesContract[];
  product: SupportingProduct;
};

export function transformSettlementsToMultiChartData({
  data,
  dateRange,
  selectedContracts,
  product,
}: TransformSettlementsToMultiChartData): DataPoint[] {
  const filteredData = data.data.filter(
    (item) =>
      item.Product === product &&
      selectedContracts.includes(item.Period) &&
      item.Timestamp >= new Date(dateRange.from).getTime() &&
      item.Timestamp <= new Date(dateRange.to).getTime(),
  );

  // Group the filtered data by timestamp
  const groupedByTimestamp = filteredData.reduce<Record<number, DataPoint>>((acc, item) => {
    const timestamp = new Date(item.Timestamp).getTime();
    if (!acc[timestamp]) {
      acc[timestamp] = { timestamp };
    }
    acc[timestamp][item.Period] = item.Price;
    return acc;
  }, {});

  // Convert the grouped data to an array of DataPoints
  const mergedData: DataPoint[] = Object.values(groupedByTimestamp);

  // Sort the merged data by timestamp
  return mergedData.sort((a, b) => a.timestamp - b.timestamp);
}
type GetUniqueContractTypes = {
  contractType: SparkSpreadFuturesContractType[];
  data: ReadSparkspreadPricesApiV1SparkspreadGetResponse;
  dateRange: {
    from: Date;
    to: Date;
  };
};
export const getUniqueContractTypes = ({ contractType, data, dateRange }: GetUniqueContractTypes) => {
  if (contractType.length === 0) {
    return [];
  }
  return [
    ...new Set(
      data.data
        .filter(
          (item) =>
            contractType.some((type) => item.Rel_code.startsWith(type.charAt(0))) &&
            item.Timestamp >= new Date(dateRange.from).getTime() &&
            item.Timestamp <= new Date(dateRange.to).getTime(),
        )
        .sort((a, b) => getPeriodOrder(b.Period) - getPeriodOrder(a.Period))
        .map((item) => item.Period),
    ),
  ];
};
